import React from 'react';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common-config/constants';
import { formatInLanguage } from '@common/config/moment-locales';
import { useAppDispatch } from '@common-reducers/hooks/store.hook';
import { DBSessionThunks } from '@common/reducers/DBServiceThunks';
import { backendApi } from '@common/reducers/backend-api/backend-api';
import {
  TableCell,
  TableRow,
} from "@/components/ui/table";
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import {
  Trash2,
  RotateCcw,
  QrCode,
  Computer,
  Server,
  Clock,
  AlertTriangle,
  PlugZap,
  Loader2,
  MoreHorizontal,
} from 'lucide-react';
import { toast } from 'sonner';
import { DisplayEngine, ENGINE_STATUS_COLORS } from '../types';
import { showRowConnect, restartOutcome, RestartSnapshot, statusHint } from '../pairing-connect';

/** Past this, a restart that has not reconnected is reported instead of spinning forever. */
const RESTART_TIMEOUT_MS = 120_000;

interface EngineRowProps {
  engine: DisplayEngine;
  /** Open the connect flow (QR / link with phone number). */
  onConnect: (engineId: string) => void;
  onDelete: (engine: DisplayEngine) => void;
}

const EngineRowComponent: React.FC<EngineRowProps> = ({ engine, onConnect, onDelete }) => {
  const { t, i18n } = useTranslation(namespace);
  const [restartK8sEngine] = backendApi.useRestartK8sEngineMutation();
  const [updateSession] = backendApi.useUpdateSessionMutation();
  const dispatch = useAppDispatch();

  // A restart in progress, from the click until the pod proves it is back (see restartOutcome).
  // It is NOT tied to the request: the request returns in about a second, the reconnect takes a
  // few more, and the displayed status masks that blip entirely — so without this the row showed
  // "Connected" throughout and the user could not tell anything had happened.
  const [restart, setRestart] = React.useState<RestartSnapshot | null>(null);
  const toastId = `engine-restart-${engine.id}`;
  const session = engine.session;

  React.useEffect(() => {
    if (!restart) return;
    const outcome = restartOutcome(restart, { status: session?.status, connectedAt: session?.connectedAt, qrCode: engine.qrCode });
    if (outcome === 'reconnected') {
      setRestart(null);
      toast.success(t('enginesManagement.restartFlow.doneToast', { defaultValue: 'Reconnected — your WhatsApp is back online.' }), { id: toastId });
    } else if (outcome === 'needs_scan') {
      setRestart(null);
      toast.info(t('enginesManagement.restartFlow.needsScanToast', { defaultValue: 'Restarted — scan the QR code to link your WhatsApp again.' }), { id: toastId });
      onConnect(engine.id);
    }
  }, [restart, session?.status, session?.connectedAt, engine.qrCode, engine.id, onConnect, t, toastId]);

  // While restarting: poll (MQTT usually pushes first) and give up loudly after the timeout.
  React.useEffect(() => {
    if (!restart) return;
    const poll = setInterval(() => dispatch(DBSessionThunks.find({})), 3000);
    const timeout = setTimeout(() => {
      setRestart(null);
      toast.warning(t('enginesManagement.restartFlow.slowToast', { defaultValue: 'The restart is taking longer than usual. Check the status below, or restart again.' }), { id: toastId });
    }, RESTART_TIMEOUT_MS);
    return () => {
      clearInterval(poll);
      clearTimeout(timeout);
    };
  }, [restart, dispatch, t, toastId]);

  const getStatusBadge = (status: string) => {
    if (restart) {
      return (
        <Badge variant="outline" className="border-0 text-white gap-1" style={{ backgroundColor: 'var(--status-info)' }}>
          <Loader2 className="h-3 w-3 animate-spin" />
          {t('enginesManagement.restartFlow.badge', { defaultValue: 'Restarting…' })}
        </Badge>
      );
    }
    const color = ENGINE_STATUS_COLORS[status as keyof typeof ENGINE_STATUS_COLORS] || 'var(--status-muted)';
    // i18next returns the KEY PATH when a label is missing, and that string is truthy — so the
    // `|| status` fallback never fired and users saw "enginesManagement.statusLabels.PAIRING" on the
    // row. defaultValue is the only fallback i18next honours.
    const label = t(`enginesManagement.statusLabels.${status}`, { defaultValue: status });

    return (
      <Badge
        variant="outline"
        className="border-0 text-white"
        style={{ backgroundColor: color }}
      >
        {label}
      </Badge>
    );
  };

  // One plain-language line under the badge: what is happening, or what to do next.
  const hintKey = engine.type === 'whatsapp-web' ? null : restart ? 'hintRestarting' : statusHint(engine.status);
  const hintDefaults: Record<string, string> = {
    hintRestarting: 'Reconnecting — no need to scan again. Usually under a minute.',
    hintScan: 'Scan the QR code with WhatsApp on your phone to finish linking.',
    hintPairing: 'Enter the pairing code in WhatsApp on your phone.',
    hintStarting: 'Starting up — usually under a minute.',
    hintSyncing: 'Linked! Syncing your chats — almost ready.',
    hintDisconnected: 'Not linked to WhatsApp — reconnect to keep sending.',
  };
  const hintBusy = hintKey === 'hintRestarting' || hintKey === 'hintStarting' || hintKey === 'hintSyncing';

  const getEngineIcon = () => {
    if (engine.type === 'whatsapp-web') {
      return <Computer size={20} className="text-orange-500" />;
    } else if (engine.type === 'gateway' || engine.type === 'whatsapp') {
      return <Server size={20} className="text-green-500" />;
    }
    return <Server size={20} className="text-gray-500" />;
  };

  // The row's one primary action. When a QR is waiting, say so — "Connect" next to "QR Code
  // Ready" read as two different things, and the QR shortcut only appeared once the QR string
  // had reached this tab's store (often it hadn't), so users saw the status but no way to scan.
  const connectLabel = engine.status === 'qrcode'
    ? t('enginesManagement.pairing.scanQr', { defaultValue: 'Scan QR code' })
    : t('enginesManagement.pairing.connect', { defaultValue: 'Connect' });

  const handleDeleteClick = () => {
    if (!engine.canDelete) {
      return;
    }
    onDelete(engine);
  };

  const handleRestartEngine = async () => {
    if (!engine.canRestart || !session || restart) {
      return;
    }

    setRestart({ connectedAt: session.connectedAt, qrCode: engine.qrCode });
    toast.loading(t('enginesManagement.restartFlow.startedToast', { defaultValue: 'Restarting your connection…' }), { id: toastId });
    try {
      const result = await restartK8sEngine(session._id).unwrap();
      if (result?.recreated) {
        // The pod was already gone, so the backend rebuilt it from scratch: that always needs a
        // fresh scan. Go straight to the QR flow instead of waiting for a reconnect that can't come.
        setRestart(null);
        toast.info(t('enginesManagement.restartFlow.recreatedToast', { defaultValue: 'Your connection was rebuilt — scan the new QR code to link it.' }), { id: toastId });
        onConnect(engine.id);
        return;
      }
      await updateSession({
        id: session._id,
        status: 'initializing',
        lastRestart: new Date().toISOString(),
      }).unwrap();
      dispatch(DBSessionThunks.find({}));
    } catch (error) {
      console.error(t('enginesManagement.restartEngineError'), error);
      setRestart(null);
      toast.error(t('enginesManagement.restartEngineError'), { id: toastId });
    }
  };

  return (
    <TableRow
      key={engine.id}
      className="hover:bg-opacity-50"
      style={{
        transition: 'background-color 0.2s ease'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = 'var(--tasks-row-hover-bg)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
    >
      <TableCell>
        <div className="flex items-center space-x-3">
          {getEngineIcon()}
          <div>
            <div className="font-medium text-sm">{engine.name}</div>
            <div className="text-xs text-muted-foreground font-mono">
              {engine.id.slice(-8)}
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-col items-start gap-1.5">
          {getStatusBadge(engine.status)}
          {hintKey && (
            <span className="text-xs flex items-center gap-1.5 min-w-[180px] max-w-[260px] whitespace-normal leading-snug" style={{ color: 'var(--color-text-muted)' }}>
              {hintBusy && <Loader2 className="h-3 w-3 animate-spin flex-none" style={{ color: 'var(--status-info)' }} />}
              {t(`enginesManagement.restartFlow.${hintKey}`, { defaultValue: hintDefaults[hintKey] })}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="text-sm">
          <div className="font-medium">{engine.domain}</div>
          <div className="text-xs text-muted-foreground">
            {engine.type === 'whatsapp-web' ? t('enginesManagement.browserExtension') : t('enginesManagement.serverGateway')}
          </div>
          {engine.isLegacy && (
            <div className="flex items-center gap-1 mt-1 text-xs text-orange-600">
              <AlertTriangle className="h-3 w-3" />
              <span>{t('enginesManagement.legacyWarning')}</span>
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>
            {/* A bare toLocaleString() takes the BROWSER's locale, not the reader's, and it
                emitted "9/10/2026, 7:37:43 AM" into a Hebrew RTL table — where the comma and the
                AM/AM marker reorder away from the numbers they belong to. */}
            {formatInLanguage(engine.connectionTime, i18n.language, 'D MMM YYYY, HH:mm')}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="flex items-center justify-end space-x-2">
          {/* Always offered here, even for the gateway the guide card above points at: the card is one
              shared CTA, and a row in "Waiting for scan" with no button of its own read as a dead end. */}
          {showRowConnect(engine, !!restart) && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 text-white"
              style={{ backgroundColor: 'var(--cta-button-bg)', borderColor: 'var(--cta-button-bg)', color: '#ffffff' }}
              onClick={(e) => {
                e.stopPropagation();
                onConnect(engine.id);
              }}
              aria-label={connectLabel}
            >
              {engine.status === 'qrcode' ? <QrCode className="h-3.5 w-3.5" /> : <PlugZap className="h-3 w-3" />}
              <span className="hidden sm:inline">{connectLabel}</span>
            </Button>
          )}
          {/* Restart / Delete are rare, recovery-only moves — tucked into a menu so the row shows
              only the one thing the user should do now (Connect / Scan QR), or nothing at all. */}
          {(engine.canRestart || engine.canDelete) && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  aria-label={t('enginesManagement.moreActions', { defaultValue: 'More actions' })}
                  onClick={(e) => e.stopPropagation()}
                >
                  {restart ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                {engine.canRestart && (
                  <DropdownMenuItem disabled={!!restart} onSelect={() => handleRestartEngine()} className="items-start py-2">
                    <RotateCcw className="h-4 w-4 mt-0.5" />
                    <div>
                      <div className="font-medium">{t('enginesManagement.restartFlow.menuRestart', { defaultValue: 'Restart connection' })}</div>
                      <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {t('enginesManagement.restartFlow.menuHint', { defaultValue: 'Fixes most sending problems. No need to scan again.' })}
                      </div>
                    </div>
                  </DropdownMenuItem>
                )}
                {engine.canRestart && engine.canDelete && <DropdownMenuSeparator />}
                {engine.canDelete && (
                  <DropdownMenuItem onSelect={() => handleDeleteClick()} className="items-start py-2" style={{ color: 'var(--status-error)' }}>
                    <Trash2 className="h-4 w-4 mt-0.5" style={{ color: 'var(--status-error)' }} />
                    <div>
                      <div className="font-medium">{t('enginesManagement.restartFlow.menuDelete', { defaultValue: 'Delete connection' })}</div>
                      <div className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                        {t('enginesManagement.restartFlow.deleteMenuHint', { defaultValue: 'Stops sending. Adding it back needs a new scan.' })}
                      </div>
                    </div>
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

// Memoized: the parent re-renders on every 30s `now` tick, but selectDisplayEngines now
// returns stable engine object references when nothing changed (deep resultEqualityCheck),
// so a tick with no real change gives EngineRow the same `engine` prop + stable handlers
// (useCallback'd in the page) → the row skips re-rendering entirely.
export const EngineRow = React.memo(EngineRowComponent);
